// src/components/Navbar.js
"use client";

import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/listings", label: "Listings" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close mobile sheet when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 shadow-sm"
          : "bg-white"
      }`}
    >
      {/* NAVBAR WRAPPER (keeps original logo placement) */}
      <div className="flex items-center justify-between px-4 sm:px-6 md:px-10 py-3">
        {/* LEFT: LOGO (unchanged placement/structure) */}
        <div className="flex items-center gap-4">
          <img
            src="/logo.svg"
            alt="Logo"
            width={150}
            height={150}
            className="object-contain"
          />
        </div>

        {/* DESKTOP NAV LINKS */}
        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3 py-2 text-[15px] rounded-lg transition-colors ${
                isActive(item.to)
                  ? "text-blue-700 bg-blue-50"
                  : "text-gray-700 hover:text-blue-700 hover:bg-gray-50"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* DESKTOP RIGHT: EMAIL + CTAs */}
        <div className="hidden md:flex items-center gap-2">
          <a
            href="mailto:info@maoqdigital.com"
            className="text-sm font-semibold text-gray-800 hover:text-blue-700"
          >
            info@maoqdigital.com
          </a>
          <Link
            to="/listings"
            className="inline-flex items-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
          >
            View Listings
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 transition"
          >
            Share a Deal
          </Link>
        </div>

        {/* MOBILE MENU BUTTON */}
        <div className="md:hidden flex items-center">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="text-gray-700 hover:text-blue-700 hover:bg-gray-100 p-2 rounded-lg transition"
            aria-label="Toggle menu"
          >
            {menuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>
      </div>

      {/* MOBILE NAV MENU */}
      {menuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <nav className="flex flex-col px-6 py-4 space-y-1">
            {LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-lg px-3 py-3 text-[15px] transition-colors ${
                  isActive(item.to)
                    ? "text-blue-700 bg-blue-50"
                    : "text-gray-800 hover:text-blue-700 hover:bg-gray-50"
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile CTAs */}
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Link
                to="/listings"
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
              >
                View Listings
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 transition"
              >
                Share a Deal
              </Link>
            </div>

            <a
              href="mailto:info@maoqdigital.com"
              className="mt-3 inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-gray-800 hover:text-blue-700 transition"
            >
              info@maoqdigital.com
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
